import React from "react";

import type { OfferQuantityButtonType } from "@intelligems/ig-types";
import { styled } from "@mui/material";
import { NewButtonRoot } from "@src/QuantityButtonsView/components/NewButtonRoot";
import { QuantityButtonsWrapper } from "@src/QuantityButtonsView/components/QuantityButtonsWrapper";
import {
  ButtonTitle,
  ComparePrice,
  FreeLabel,
  Label,
  Price,
  QuantityButtonWidgetVariantWrapper,
  SectionTitle,
  TitleSection,
} from "@src/QuantityButtonsView/components/sharedComponents";

import { useQuantityButtonsContext } from "../context/QuantityButtonsContext";

export const QuantityButtonsWidgetCompact = () => {
  const { widget } = useQuantityButtonsContext();

  const renderFreeShippingLabelPlaceholder = widget.variables.buttons
    .filter((button) => !button.freeShipping?.hide)
    .some((button) => button.freeShipping?.freeShippingLabel);

  const renderFreeGiftLabelPlaceholder = widget.variables.buttons
    .filter((button) => !button.freeGift?.hide)
    .some((button) => button.freeGift?.freeGiftLabel);

  const renderLabelPlaceholder = widget.variables.buttons.some(
    (button) => button.label
  );

  const longestLabel = !renderLabelPlaceholder
    ? "."
    : widget.variables.buttons
        .map((button) => button.label || ".")
        .reduce((longest, current) => {
          return current.length > longest.length ? current : longest;
        }, "");

  return (
    <QuantityButtonWidgetVariantWrapper
      buttonVariant={"quantityButtonsWidgetCompact"}
    >
      {widget.variables?.sectionTitle && <SectionTitle />}

      <QuantityButtonsWrapper
        renderButton={({ button, selected, index }) => (
          <NewButtonRoot
            key={index}
            button={button}
            selected={selected}
            renderContent={({ button, newTotalPrice, totalComparePrice }) => (
              <div className={"ig-quantity-widget-root-wrapper"}>
                <ButtonContent>
                  {widget.styles.appearance.layout === "row" ? (
                    <RowLayout
                      selected={selected}
                      renderLabelPlaceholder={renderLabelPlaceholder}
                      button={button}
                      newTotalPrice={newTotalPrice}
                      renderFreeGiftLabelPlaceholder={
                        renderFreeGiftLabelPlaceholder
                      }
                      renderFreeShippingLabelPlaceholder={
                        renderFreeShippingLabelPlaceholder
                      }
                      longestLabel={longestLabel}
                      totalComparePrice={totalComparePrice}
                    />
                  ) : (
                    <ColumnLayout
                      selected={selected}
                      button={button}
                      newTotalPrice={newTotalPrice}
                      renderLabelPlaceholder={renderLabelPlaceholder}
                      renderFreeGiftLabelPlaceholder={
                        renderFreeGiftLabelPlaceholder
                      }
                      totalComparePrice={totalComparePrice}
                      renderFreeShippingLabelPlaceholder={
                        renderFreeShippingLabelPlaceholder
                      }
                    />
                  )}
                </ButtonContent>
              </div>
            )}
          />
        )}
      />
    </QuantityButtonWidgetVariantWrapper>
  );
};

const ButtonContent = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",
  };
});

const ButtonLineContent = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    display: "flex",
    flexDirection: widget.styles.appearance.layout === "row" ? "column" : "row",
    alignItems:
      widget.styles.appearance.layout === "row" ? "flex-start" : "center",
    justifyContent:
      widget.styles.appearance.layout === "row"
        ? "flex-start"
        : "space-between",
  };
});

const ContentSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "2px",
});
const ColumnLayout = ({
  button,
  newTotalPrice,
  renderLabelPlaceholder,
  renderFreeGiftLabelPlaceholder,
  renderFreeShippingLabelPlaceholder,
  selected,
  totalComparePrice,
}: {
  button: OfferQuantityButtonType;
  newTotalPrice: string | undefined;
  renderLabelPlaceholder: boolean;
  renderFreeShippingLabelPlaceholder: boolean;
  renderFreeGiftLabelPlaceholder: boolean;
  selected: boolean;
  totalComparePrice: string | undefined;
}) => {
  return (
    <ContentSection id={"content-section"}>
      <ButtonLineContent>
        <TitleSection>
          <ButtonTitle>
            {button.buttonTitle ||
              `${button.quantity} ${button.unitName || "item"}`}
          </ButtonTitle>
          <Label hasLabel={!!button.label}>{button.label || "."}</Label>
        </TitleSection>
        <Price>{newTotalPrice}</Price>
      </ButtonLineContent>
      {button.showCompareAtPrice && totalComparePrice && (
        <ButtonLineContent style={{ justifyContent: "flex-end" }}>
          <ComparePrice>{totalComparePrice}</ComparePrice>
        </ButtonLineContent>
      )}

      {!renderFreeGiftLabelPlaceholder &&
      !renderFreeShippingLabelPlaceholder ? null : (
        <ButtonLineContent style={{ gap: "4px", justifyContent: "flex-start" }}>
          {!renderFreeShippingLabelPlaceholder ||
          button.freeShipping?.hide ||
          (button.freeShipping?.showOnlyWhenButtonSelected &&
            !selected) ? null : (
            <FreeLabel
              labelType={"freeShipping"}
              hasLabel={!!button.freeShipping?.freeShippingLabel}
            >
              {button.freeShipping?.freeShippingLabel || "."}
            </FreeLabel>
          )}
          {!renderFreeGiftLabelPlaceholder ||
          button.freeGift?.hide ||
          (button.freeGift?.showOnlyWhenButtonSelected && !selected) ? null : (
            <FreeLabel
              labelType={"freeGift"}
              hasLabel={!!button.freeGift?.freeGiftLabel}
            >
              {button.freeGift?.freeGiftLabel || "."}
            </FreeLabel>
          )}
        </ButtonLineContent>
      )}
    </ContentSection>
  );
};

const RowLayout = ({
  button,
  newTotalPrice,
  renderLabelPlaceholder,
  renderFreeGiftLabelPlaceholder,
  renderFreeShippingLabelPlaceholder,
  longestLabel,
  selected,
  totalComparePrice,
}: {
  button: OfferQuantityButtonType;
  newTotalPrice: string | undefined;
  renderLabelPlaceholder: boolean;
  renderFreeShippingLabelPlaceholder: boolean;
  renderFreeGiftLabelPlaceholder: boolean;
  longestLabel: string;
  selected: boolean;
  totalComparePrice: string | undefined;
}) => {
  return (
    <ContentSection style={{ gap: "12px" }}>
      <ButtonLineContent>
        <TitleSection
          style={{
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <ButtonTitle>
            {button.buttonTitle ||
              `${button.quantity} ${button.unitName || "item"}`}
          </ButtonTitle>
          {!renderLabelPlaceholder ? null : (
            <Label hasLabel={!!button.label}>
              {button.label ?? longestLabel}
            </Label>
          )}
        </TitleSection>
      </ButtonLineContent>
      {!renderFreeGiftLabelPlaceholder &&
      !renderFreeShippingLabelPlaceholder ? null : (
        <ButtonLineContent style={{ gap: "4px" }}>
          {!renderFreeShippingLabelPlaceholder ||
          button.freeShipping?.hide ||
          (button.freeShipping?.showOnlyWhenButtonSelected &&
            !selected) ? null : (
            <FreeLabel
              labelType={"freeShipping"}
              hasLabel={!!button.freeShipping?.freeShippingLabel}
            >
              {button.freeShipping?.freeShippingLabel || "."}
            </FreeLabel>
          )}
          {!renderFreeGiftLabelPlaceholder ||
          button.freeGift?.hide ||
          (button.freeGift?.showOnlyWhenButtonSelected && !selected) ? null : (
            <FreeLabel
              labelType={"freeGift"}
              hasLabel={!!button.freeGift?.freeGiftLabel}
            >
              {button.freeGift?.freeGiftLabel || "."}
            </FreeLabel>
          )}
        </ButtonLineContent>
      )}
      <ButtonLineContent>
        <Price>{newTotalPrice}</Price>
        {button.showCompareAtPrice && totalComparePrice && (
          <ComparePrice>{totalComparePrice}</ComparePrice>
        )}
      </ButtonLineContent>
    </ContentSection>
  );
};
